/** Qualifying methods policy — Setup artifact pinned by leasing season config. */

/** Canonical method ids (aligned with pipeline / workbook answers). */
export type QualifyingMethodId =
    | 'self_qualified'
    | 'personal_guarantor'
    | 'leap_gwp'
    | 'enhanced_deposit'
    | 'rent_prepay'

/** @deprecated Use QualifyingMethodId — kept for Compose timeline imports. */
export type ConditionalMethodId = QualifyingMethodId

export type MethodAvailability = 'upfront' | 'after_failed_screen' | 'both'

/** Who picks a fallback method after screening fails. */
export type FallbackChoiceMode = 'applicant' | 'property_manager'

export type DepositMode = 'rent_multiple' | 'fixed'
export type UpfrontRentMode = 'none' | 'first' | 'first_and_last'
export type ExtraRentMode = 'none' | 'fixed' | 'percent'

export type EnhancedDepositConfig = {
    depositMode: DepositMode
    /** Used when depositMode is rent_multiple (e.g. 2 = 2× monthly rent). */
    depositRentMultiple: number
    /** Used when depositMode is fixed (dollars as string for input). */
    depositFixedAmount: string
}

export type RentPrepayConfig = {
    upfrontRent: UpfrontRentMode
    extraRentMode: ExtraRentMode
    extraRentFixed: string
    extraRentPercent: string
}

type BaseMethod = {
    enabled: boolean
    availability: MethodAvailability
}

export type QualifyingMethodsPolicy = {
    /** Who chooses among after-fail methods when screening does not pass. */
    fallbackChoiceMode: FallbackChoiceMode
    self_qualified: BaseMethod
    personal_guarantor: BaseMethod
    leap_gwp: BaseMethod
    enhanced_deposit: BaseMethod & EnhancedDepositConfig
    rent_prepay: BaseMethod & RentPrepayConfig
}

/** @deprecated Prefer QualifyingMethodsPolicy */
export type ConditionalApprovalsPolicy = QualifyingMethodsPolicy

export const QUALIFYING_METHOD_IDS: QualifyingMethodId[] = [
    'self_qualified',
    'personal_guarantor',
    'leap_gwp',
    'enhanced_deposit',
    'rent_prepay',
]

/** Methods that only make sense as post-screen fallbacks by default. */
export const FALLBACK_ORIENTED_METHOD_IDS: QualifyingMethodId[] = [
    'leap_gwp',
    'enhanced_deposit',
    'rent_prepay',
]

export const AVAILABILITY_LABELS: Record<MethodAvailability, string> = {
    upfront: 'Upfront (initial choice)',
    after_failed_screen: 'After failed screening',
    both: 'Both (upfront and after fail)',
}

export const QUALIFYING_METHOD_META: Record<
    QualifyingMethodId,
    { label: string; summary: string; info: string }
> = {
    self_qualified: {
        label: 'Self qualified',
        summary: 'Applicant meets credit / income requirements on their own.',
        info:
            'Usually offered upfront. Screening runs on the applicant; if they pass, no fallback package is needed.',
    },
    personal_guarantor: {
        label: 'Personal guarantor',
        summary:
            'A parent or other adult guarantor is screened and signs a guaranty.',
        info:
            'Standard student-housing path. Guarantor contact fields in Applicant Data apply when this method is chosen.',
    },
    leap_gwp: {
        label: 'Leap / GWP',
        summary:
            'Third-party Guarantor Waiver Program — monthly fee billed through the property.',
        info:
            'Can be offered upfront (skip self-screen intent) and/or after a failed screen. Requires Leap setup for the property.',
    },
    enhanced_deposit: {
        label: 'Enhanced security deposit',
        summary:
            'Approve via an additional security deposit above the standard amount.',
        info:
            'Typically a last-resort fallback after screening does not pass. Set multiple of rent or a fixed amount.',
    },
    rent_prepay: {
        label: 'Rent prepayment',
        summary:
            'Approve via upfront rent and/or a higher monthly rent amount.',
        info:
            'Typically a last-resort fallback. Choose first / first-and-last month up front, and optional extra monthly rent.',
    },
}

/** @deprecated Prefer QUALIFYING_METHOD_META */
export const CONDITIONAL_METHOD_META = QUALIFYING_METHOD_META

export function defaultEnhancedDepositConfig(): EnhancedDepositConfig {
    return {
        depositMode: 'rent_multiple',
        depositRentMultiple: 2,
        depositFixedAmount: '',
    }
}

export function defaultRentPrepayConfig(): RentPrepayConfig {
    return {
        upfrontRent: 'first_and_last',
        extraRentMode: 'none',
        extraRentFixed: '',
        extraRentPercent: '',
    }
}

export function defaultConditionalApprovalsPolicy(): QualifyingMethodsPolicy {
    return {
        fallbackChoiceMode: 'applicant',
        self_qualified: {
            enabled: true,
            availability: 'upfront',
        },
        personal_guarantor: {
            enabled: true,
            availability: 'upfront',
        },
        leap_gwp: {
            enabled: false,
            availability: 'both',
        },
        enhanced_deposit: {
            enabled: true,
            availability: 'after_failed_screen',
            ...defaultEnhancedDepositConfig(),
        },
        rent_prepay: {
            enabled: true,
            availability: 'after_failed_screen',
            ...defaultRentPrepayConfig(),
        },
    }
}

export function normalizeQualifyingMethodsPolicy(
    raw: unknown,
): QualifyingMethodsPolicy {
    const base = defaultConditionalApprovalsPolicy()
    if (!raw || typeof raw !== 'object') return base
    const src = raw as Record<string, unknown>

    const mode = src.fallbackChoiceMode
    if (mode === 'applicant' || mode === 'property_manager') {
        base.fallbackChoiceMode = mode
    }

    for (const id of QUALIFYING_METHOD_IDS) {
        const incoming = src[id]
        if (!incoming || typeof incoming !== 'object') continue
        const row = incoming as Record<string, unknown>
        const current = base[id] as Record<string, unknown>
        const next = { ...current, ...row }
        if (typeof row.enabled === 'boolean') next.enabled = row.enabled
        if (
            row.availability === 'upfront' ||
            row.availability === 'after_failed_screen' ||
            row.availability === 'both'
        ) {
            next.availability = row.availability
        } else if (current.availability == null) {
            // Legacy rows (no availability): primary paths upfront, packages after fail.
            next.availability =
                id === 'self_qualified' || id === 'personal_guarantor'
                    ? 'upfront'
                    : id === 'leap_gwp'
                    ? 'both'
                    : 'after_failed_screen'
        }
        const methods = base as Record<string, unknown>
        methods[id] = next
    }

    return base
}

function availabilityIncludes(
    availability: MethodAvailability,
    stage: 'upfront' | 'after_failed_screen',
): boolean {
    if (availability === 'both') return true
    return availability === stage
}

export function enabledQualifyingMethods(
    policy: QualifyingMethodsPolicy | undefined,
): QualifyingMethodId[] {
    if (!policy) return []
    return QUALIFYING_METHOD_IDS.filter((id) => policy[id].enabled)
}

/** @deprecated Prefer enabledQualifyingMethods */
export function enabledConditionalMethods(
    policy: QualifyingMethodsPolicy | undefined,
): QualifyingMethodId[] {
    return enabledQualifyingMethods(policy)
}

export function methodsForStage(
    policy: QualifyingMethodsPolicy | undefined,
    stage: 'upfront' | 'after_failed_screen',
): QualifyingMethodId[] {
    if (!policy) return []
    return QUALIFYING_METHOD_IDS.filter(
        (id) =>
            policy[id].enabled &&
            availabilityIncludes(policy[id].availability, stage),
    )
}

/** Select option labels for workbook `qualifying_method` / fallback fields. */
export function qualifyingMethodOptionLabels(
    policy: QualifyingMethodsPolicy | undefined,
    stage: 'upfront' | 'after_failed_screen',
): string[] {
    return methodsForStage(policy, stage).map(
        (id) => QUALIFYING_METHOD_META[id].label,
    )
}

/** Stable option values (method ids) for machine-readable answers. */
export function qualifyingMethodOptionValues(
    policy: QualifyingMethodsPolicy | undefined,
    stage: 'upfront' | 'after_failed_screen',
): string[] {
    return methodsForStage(policy, stage)
}

export function conditionalApprovalsReadiness(
    policy: QualifyingMethodsPolicy | undefined,
): string {
    const enabled = enabledQualifyingMethods(policy).length
    const total = QUALIFYING_METHOD_IDS.length
    const upfront = methodsForStage(policy, 'upfront').length
    const fallback = methodsForStage(policy, 'after_failed_screen').length
    if (enabled === 0) return 'No methods enabled'
    return `${enabled}/${total} on · ${upfront} upfront · ${fallback} after fail`
}

export const QUALIFYING_METHOD_FIELD_ID = 'qualifying_method'
export const FALLBACK_QUALIFYING_METHOD_FIELD_ID = 'fallback_qualifying_method'
export const FAILED_QUALIFICATION_BUCKET_ID = 'failed_qualification'

const FAILED_QUALIFICATION_LABEL = 'Failed Qualification'
const FAILED_QUALIFICATION_SUMMARY =
    'After-fail qualifying methods when the applicant chooses among them.'
const FALLBACK_QUALIFYING_METHOD_QUESTION =
    'You did not pass qualification. Which method would you like to use?'

/** Applicant Data fields owned by Qualifying Methods (locked in the section editor). */
export const POLICY_LOCKED_QUALIFYING_FIELD_IDS: string[] = [
    QUALIFYING_METHOD_FIELD_ID,
    FALLBACK_QUALIFYING_METHOD_FIELD_ID,
]

export function isPolicyLockedQualifyingField(fieldId: string): boolean {
    return POLICY_LOCKED_QUALIFYING_FIELD_IDS.includes(fieldId)
}

type SectionFieldLike = {
    field_id: string
    options?: string[]
    question_text?: string
    input_type?: string
    enabled?: boolean
    required?: boolean
    [key: string]: unknown
}

type SectionLike = {
    bucket_id?: string
    label?: string
    summary?: string
    kind?: string
    configured?: boolean
    enabled?: boolean
    fields?: SectionFieldLike[]
    [key: string]: unknown
}

function stripFallbackField(
    fields: SectionFieldLike[] | undefined,
): {
    fields: SectionFieldLike[]
    removed?: SectionFieldLike
} {
    const list = fields ?? []
    let removed: SectionFieldLike | undefined
    const next = list.filter((field) => {
        if (field.field_id === FALLBACK_QUALIFYING_METHOD_FIELD_ID) {
            removed = field
            return false
        }
        return true
    })
    return { fields: next, removed }
}

/**
 * Patch Applicant Data so qualifying-method selects match policy, and keep the
 * Failed Qualification section in sync with fallbackChoiceMode:
 * - applicant → section enabled with required after-fail question
 * - property_manager → section disabled / hidden (no applicant choice)
 */
export function applyQualifyingMethodOptionsToSections<T extends object>(
    sections: T,
    policy: QualifyingMethodsPolicy | undefined,
): T {
    const upfront = qualifyingMethodOptionLabels(policy, 'upfront')
    const afterFail = qualifyingMethodOptionLabels(
        policy,
        'after_failed_screen',
    )
    const applicantChoosesFallback =
        !policy || policy.fallbackChoiceMode === 'applicant'

    const next = { ...sections } as T
    const asMap = next as Record<string, SectionLike>
    let movedFallback: SectionFieldLike | undefined

    for (const key of Object.keys(asMap)) {
        if (key === FAILED_QUALIFICATION_BUCKET_ID) continue
        const section = asMap[key]
        if (!section) continue
        const stripped = stripFallbackField(section.fields)
        if (stripped.removed) movedFallback = stripped.removed
        let fields = stripped.fields
        let touched = stripped.removed != null
        fields = fields.map((field) => {
            if (field.field_id === QUALIFYING_METHOD_FIELD_ID) {
                touched = true
                return { ...field, options: [...upfront] }
            }
            return field
        })
        if (touched) {
            asMap[key] = { ...section, fields }
        }
    }

    if (applicantChoosesFallback) {
        const existing = asMap[FAILED_QUALIFICATION_BUCKET_ID]
        const baseField: SectionFieldLike = {
            field_id: FALLBACK_QUALIFYING_METHOD_FIELD_ID,
            question_text:
                movedFallback?.question_text ||
                existing?.fields?.find(
                    (f) => f.field_id === FALLBACK_QUALIFYING_METHOD_FIELD_ID,
                )?.question_text ||
                FALLBACK_QUALIFYING_METHOD_QUESTION,
            input_type: 'select',
            enabled: true,
            required: true,
            options: [...afterFail],
        }
        const otherFields = (existing?.fields ?? []).filter(
            (f) => f.field_id !== FALLBACK_QUALIFYING_METHOD_FIELD_ID,
        )
        asMap[FAILED_QUALIFICATION_BUCKET_ID] = {
            ...(existing ?? {}),
            bucket_id: FAILED_QUALIFICATION_BUCKET_ID,
            label: existing?.label || FAILED_QUALIFICATION_LABEL,
            summary: existing?.summary || FAILED_QUALIFICATION_SUMMARY,
            kind: 'platform',
            configured: true,
            enabled: true,
            fields: [baseField, ...otherFields],
        }
    } else {
        const existing = asMap[FAILED_QUALIFICATION_BUCKET_ID]
        asMap[FAILED_QUALIFICATION_BUCKET_ID] = {
            ...(existing ?? {}),
            bucket_id: FAILED_QUALIFICATION_BUCKET_ID,
            label: existing?.label || FAILED_QUALIFICATION_LABEL,
            summary: existing?.summary || FAILED_QUALIFICATION_SUMMARY,
            kind: 'platform',
            configured: false,
            enabled: false,
            fields: [],
        }
    }

    return next
}
